import * as React from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import ConfirmPopup from './ConfirmPopup';
import authService from './api-authorization/AuthorizeService';
import NotesPopup from './NotesPopup';

interface AllocateTruck {
    systemCompanyId: number;
    truckOptions: TruckListOption[];
    teamLeadOptions: TeamOption[];
    driverOptions: TeamOption[];
    teamOptions: TeamOption[];
    truckAllocationItems: TruckAllocationItem[];
    showConfirmModal: boolean;
    deleteIndex: number;
    showNotes: boolean;
    showNoteType: number;   //1=Driver Notes, 2=Schedule Notes
    editNotes: EditNotes;
    saveDisabled: boolean;
    errors: any;
    loading: boolean;
}

interface AllocateTruckProps {
    scheduledDate: string;
    back(): void;
    save(): void;
}

interface TruckAllocationItem {
    id: number;
    truckId: number;
    truckBranchId: number;
    teamLeadId: number;
    driverId: number;
    teamId: number;
    teamMembers: TeamMember[];
    notes: string;
    selected: boolean;
    isDirty: boolean;
}

interface TeamMember {
    id: number;
    name: string;
}

interface TruckListOption {
    id: number;
    branchId: number;
    name: string;
    registrationNumber: string;
}

interface TeamOption {
    id: number;
    name: string;
    branches: TeamBranch[];
}

interface TeamBranch {
    id: number;
}

interface EditNotes {
    heading: string;
    rowIndex: number;
    parentIndex: number;
    id: number;
    itemId: number;
    item: string;
    notes: string;
}

export default class AllocateTeamToTruck extends React.Component<AllocateTruckProps, AllocateTruck> {
    constructor(props: AllocateTruckProps) {
        super(props);

        this.state = {
            systemCompanyId: 2,
            truckOptions: [],
            teamLeadOptions: [],
            driverOptions: [],
            teamOptions: [],
            truckAllocationItems: [],
            showConfirmModal: false,
            deleteIndex: 0,
            showNotes: false,
            showNoteType: 1,
            editNotes: {
                heading: "Truck Notes",
                rowIndex: -1,
                parentIndex: -1,
                id: 0,
                itemId: -1,
                item: "",
                notes: ""
            },
            saveDisabled: false,
            errors: {},
            loading: true
        };  
    }

    componentDidMount() {
        this.getTruckAllocation();
    }

    goBack = () => {
        this.props.back();
    }

    async getTruckAllocation() {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        axios.get('api/Allocate/GetTruckAllocation?ScheduleDate=' + this.props.scheduledDate + '&SubId=' + user.sub, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            let allocationItems = res.data.truckAllocationItems;
            let newItem = this.addBlankRow();
            allocationItems.push(newItem);

            this.setState({ systemCompanyId: res.data.systemCompanyId, truckOptions: res.data.truckOptions, teamLeadOptions: res.data.truckTeamLeads, driverOptions: res.data.drivers, teamOptions: res.data.truckLabourers, truckAllocationItems: allocationItems, errors: {}, loading: false })
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    addBlankRow = () => {
        let newItem: TruckAllocationItem = {
            id: 0,
            truckId: -1,
            truckBranchId: 0,
            teamLeadId: -1,
            driverId: -1,
            teamId: 0,
            teamMembers: [],
            notes: "",
            selected: false,
            isDirty: false
        };
        return newItem;
    }

    //item ticked
    onCellCheckboxChange = (index: number, e: any) => {
        var truckAllocationItems = this.state.truckAllocationItems;
        var truckAllocationItem = truckAllocationItems[index];
        truckAllocationItem.selected = e.target.checked;

        if (e.target.checked || truckAllocationItem.id === 0) {
            this.setState({ truckAllocationItems: truckAllocationItems, saveDisabled: false });
        } else {
            //need to remove truck allocation
            truckAllocationItem.isDirty = false;    //getting deleted so don't need to validate / save 
            this.deleteTruckAllocation(truckAllocationItem.id);
        }

        
    }

    //grid item changed
    onCellChange = (index: number, item: string, e: any) => {
        var exists = false;
        var truckAllocationItems = this.state.truckAllocationItems;
        var truckAllocationItem = truckAllocationItems[index];
        truckAllocationItem.isDirty = true;

        if (item === "truckId") {
            //check if truck already allocated
            var filtered = truckAllocationItems.filter(truck => truck.truckId == e.target.value);
            if (filtered.length > 0) {
                exists = true;
            } else {
                //update the truck branch - used for filtering drivers, team leads, members
                var truckDetails = this.state.truckOptions.filter(truck => truck.id == e.target.value);
                if (truckDetails.length > 0) {
                    truckAllocationItem.truckBranchId = truckDetails[0].branchId;
                } else {
                    truckAllocationItem.truckBranchId = 0;
                }
            }
        }

        if (exists) {
            toast.error("This truck has already been allocated!");
        } else {
            //update item that has changed
            let newItem: any = {};
            newItem = truckAllocationItem;
            newItem[item] = e.target.value;
            newItem["selected"] = true;

            if ((truckAllocationItems.length - 1) == index) {
                let blankItem = this.addBlankRow();
                truckAllocationItems.push(blankItem);
            }

            this.setState({ truckAllocationItems: truckAllocationItems, saveDisabled: false, errors: {} });
        }
    }

    //add team member
    onCellTeamChange = (index: number, e: any) => {
        var items = this.state.truckAllocationItems;
        var item = items[index];
        var teamMembers = item.teamMembers;
        var newTeamMember: TeamMember = {
            id: e.target.value,
            name: e.target.selectedOptions[0].label
        };
        teamMembers.push(newTeamMember);
        item.teamId = -1;
        item.isDirty = true;
        item.selected = true;

        if ((items.length - 1) == index) {
            let blankItem = this.addBlankRow();
            items.push(blankItem);
        }

        this.setState({ truckAllocationItems: items, saveDisabled: false, errors: {} });
    }

    //remove team member
    removeItem = (index: number, id: number) => {
        var items = this.state.truckAllocationItems;
        var item = items[index];
        var teamMembers = item.teamMembers;
        teamMembers.splice(teamMembers.findIndex(function (i) {
            return i.id == id;
        }), 1);
        item.isDirty = true;

        this.setState({ truckAllocationItems: items, saveDisabled: false, errors: {} });
    }

    //NOTES
    hideNotes = () => {
        var editNotes = this.resetNotes();

        this.setState({ editNotes: editNotes, showNotes: false, showNoteType: 1 });
    }

    resetNotes = () => {
        var editNotes: EditNotes = {
            heading: "",
            parentIndex: -1,
            rowIndex: -1,
            id: 0,
            itemId: -1,
            item: "",
            notes: ""
        }

        return editNotes;
    }

    updateNotes = (editNote: EditNotes) => {
        var editNotes = this.state.editNotes;
        editNotes.notes = editNote.notes;
        this.setState({
            editNotes: editNotes
        });
    }

    editNotes = (e: any, itemIndex: number) => {
        e.preventDefault();

        var trucks = this.state.truckAllocationItems;
        var truck = trucks[itemIndex];

        var editNotes: EditNotes;
        
        //edit driver notes
        editNotes = {
            heading: "Truck Notes",
            parentIndex: 0,
            rowIndex: itemIndex,
            id: truck.id,
            itemId: 0,
            item: "",
            notes: truck.notes
        };
        
        this.setState({ editNotes: editNotes, showNotes: true, showNoteType: 1, errors: {} });
    }

    saveNotes = () => {
        var editNotes = this.state.editNotes;
        
        var trucks = this.state.truckAllocationItems;
        var truck = trucks[editNotes.rowIndex];
        truck.notes = editNotes.notes;

        this.setState({ truckAllocationItems: trucks });
        if (truck.id > 0) {
            this.saveTruckNotes(truck.id, editNotes.notes);
        } else {
            var editNotes = this.resetNotes();
            this.setState({ editNotes: editNotes, showNotes: false, showNoteType: 1, errors: {} });
        }
    }

    async saveTruckNotes(truckId: number, notes: string) {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        let truckNotes = {
            id: truckId,
            notes: notes,
            subId: user.sub
        };

        axios.post('api/Allocate/SaveTruckNotes', truckNotes, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            if (res.data.isError) {
                var errors = res.data.messages as any[];
                for (var i = 0; i < errors.length; i++) {
                    toast.error(errors[i].content);
                };
            } else {
                var editNotes = this.resetNotes();
                this.setState({ editNotes: editNotes, showNotes: false, showNoteType: 1, errors: {} });
                toast.success("Truck Notes Saved!");
            }
        })
        .catch(error => {
            toast.error(error.message);
        });
    }
    //END NOTES

    //DELETE ALLOCATION
    deleteItem = (e: any, index: number) => {
        e.preventDefault();

        this.setState({ showConfirmModal: true, deleteIndex: index });
    }

    hideConfirmModal = () => {
        this.setState({ showConfirmModal: false, deleteIndex: 0 });
    }

    saveConfirmModal = () => {
        var items = this.state.truckAllocationItems;
        var item = items[this.state.deleteIndex];

        if (item.id === 0) {
            //just remove from the list as not saved
            items.splice(this.state.deleteIndex, 1);
            this.setState({ truckAllocationItems: items, showConfirmModal: false, deleteIndex: 0, saveDisabled: false });
            toast.success("Item has been deleted");
        } else {
            this.deleteTruckAllocation(item.id);
        } 
    }

    async deleteTruckAllocation(itemId: number) {
        var items = this.state.truckAllocationItems;
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        let deleteItem = {
            Id: itemId,
            ScheduledDate: this.props.scheduledDate,
            subId: user.sub
        };

        axios.post('api/Allocate/DeleteTruckAllocation', deleteItem, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            if (res.data.isError) {
                var errors = res.data.messages as any[];
                for (var i = 0; i < errors.length; i++) {
                    toast.error(errors[i].content);
                };
                this.setState({ showConfirmModal: false, deleteIndex: 0, saveDisabled: false });
            } else {
                if (this.state.systemCompanyId === 2) {
                    //only remove from the table if it is Scaffold as I show all trucks for Dunnys
                    items.splice(this.state.deleteIndex, 1);
                }
                this.setState({ truckAllocationItems: items, showConfirmModal: false, deleteIndex: 0, saveDisabled: false });
                toast.success("Item has been deleted");
            }
        })
        .catch(error => {
            toast.error(error.message);
        });
    }
    //END DELETE ALLOCATION

    validate = () => {
        let errors: any = {};
        let formIsValid = true;

        let items = this.state.truckAllocationItems;
        var rowNumber = 0;

        var errorString = "Please enter the following values: ";
        var error = false;

        for (var i = 0; i < (items.length - 1); i++) {
            rowNumber += 1;
            var newErrorString = "";

            if (items[i].isDirty || items[i].selected) {
                if (items[i].truckId < 1) {
                    newErrorString += "Truck, ";
                    error = true;
                }

                if (items[i].driverId < 1) {
                    newErrorString += "Driver, ";
                    error = true;
                }

                if (newErrorString != "") {
                    errorString += "\n"
                    errorString += "Row " + rowNumber + ": " + newErrorString;
                    errorString = errorString.substring(0, errorString.length - 2);
                }
            }
        }
        if (error) {
            formIsValid = false;
            errors["truckAllocation"] = errorString;
        }
        this.setState({ errors: errors });
        return formIsValid;
    }

    save = (e: any) => {
        e.preventDefault();

        if (this.validate()) {
            if (this.state.saveDisabled) {
                return;
            } 
            this.setState({ saveDisabled: true });

            this.saveTruckAllocation();
        } else {
            toast.error("Fix validation errors before saving!");
        }
    }

    async saveTruckAllocation() {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        //save
        let changed = this.state.truckAllocationItems.filter(items => items.isDirty || items.selected);

        if (changed.length === 0) {
            toast.info("No items have changed!");
        } else {
            let saveItems = {
                scheduledDate: this.props.scheduledDate,
                truckAllocationItems: changed,
                subId: user.sub,
                companyId: this.state.systemCompanyId
            };

            axios.post('api/Allocate/SaveTruckAllocation', saveItems, {
                headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
            })
            .then(res => {
                if (res.data.isError) {
                    var errors = res.data.messages as any[];
                    for (var i = 0; i < errors.length; i++) {
                        toast.error(errors[i].content);
                    };
                } else {
                    toast.success("Truck Allocation Saved");
                    this.props.save();
                }
            })
            .catch(error => {
                toast.error(error.message);
            });
        }
    }

    render() {
        let contents = this.state.loading
            ? <p><em>Loading...</em></p>
            : this.renderDetails();


        return <div>
            {contents}
        </div>;
    }

    renderDetails = () => {
        let truckAllocationItems = this.state.truckAllocationItems;
        let truckOptions = this.state.truckOptions;
        let teamLeadOptions = this.state.teamLeadOptions;
        let driverOptions = this.state.driverOptions;
        let teamOptions = this.state.teamOptions;
        
        let confirmPopup = <ConfirmPopup heading="Delete Truck Team?" text="Are you sure you want to delete this item?" hideConfirmModal={this.hideConfirmModal} showConfirmModal={this.state.showConfirmModal} saveConfirmModal={this.saveConfirmModal} />
        let notesPopup = <NotesPopup showEditNotesModal={this.state.showNotes} editNotes={this.state.editNotes} hideNotes={this.hideNotes} updateNotes={this.updateNotes} saveNotes={this.saveNotes} />;

        return (
            <div>
                <div className="static-modal">
                    {confirmPopup}
                    {notesPopup}
                </div>
                <div>
                    <div className="defaultbutton__container--right allocate__backMargin">
                        <button className="defaultbutton" type="button" onClick={this.goBack}>Back</button>
                    </div>
                    <div className="table__margin overflowAuto">
                        <table className="table table__large tableColours">
                            <thead>
                                <tr>
                                    <th className="hidden">Id</th>
                                    <th></th>
                                    <th className={this.state.systemCompanyId === 3 ? "" : "hidden"}>Select</th>
                                    <th>Truck</th>
                                    <th className={this.state.systemCompanyId === 3 ? "hidden" : ""}>Team Lead</th>
                                    <th>Driver</th>
                                    <th>Team</th>
                                    <th>Team Members</th>
                                    <th>Notes</th>
                                    <th>Delete</th>
                                </tr>
                            </thead>
                            <tbody>
                                {truckAllocationItems.map((item, index) =>
                                    <tr key={index}>
                                        <td className="hidden">{item.id}</td>
                                        <td className="table__text--align">{index + 1}</td>
                                        <td className={this.state.systemCompanyId === 3 ? "table__text--align": "hidden"}>
                                            <input type='checkbox' id="selected" name="selected" className='alignIconCenter' checked={item.selected} onChange={(e) => this.onCellCheckboxChange(index, e)} />
                                        </td>
                                        <td className="table__text--align">
                                            <select className="select" id="truckId" name="truckId" value={item.truckId} onChange={(e) => this.onCellChange(index, "truckId", e)} >
                                                <option defaultValue="-1"></option>
                                                {truckOptions.map(truck =>
                                                    <option key={truck.id} value={truck.id}>{truck.name}-{truck.registrationNumber}</option>
                                                )};
                                            </select>
                                        </td>
                                        <td className={this.state.systemCompanyId === 3 ? "hidden" : "table__text--align"}>
                                            <select className="select" id="teamLeadId" name="teamLeadId" value={item.teamLeadId} onChange={(e) => this.onCellChange(index, "teamLeadId", e)} >
                                                <option defaultValue="-1"></option>
                                                {teamLeadOptions.filter(data => data.branches.find(x => x.id == item.truckBranchId)).map(lead =>
                                                    <option key={lead.id} value={lead.id}>{lead.name}</option>
                                                )}
                                            </select>
                                        </td>
                                        <td className="table__text--align">
                                            <select className="select" id="driverId" name="driverId" value={item.driverId} onChange={(e) => this.onCellChange(index, "driverId", e)} >
                                                <option defaultValue="-1"></option>
                                                {driverOptions.filter(data => data.branches.find(x => x.id == item.truckBranchId)).map(driver =>
                                                    <option key={driver.id} value={driver.id}>{driver.name}</option>
                                                )}
                                            </select>
                                        </td>
                                        <td className="table__text--align">
                                            <select className="select" id="teamId" name="teamId" value={item.teamId} onChange={(e) => this.onCellTeamChange(index, e)} >
                                                <option defaultValue="-1"></option>
                                                {teamOptions.filter(data => data.branches.find(x => x.id == item.truckBranchId)).map(team =>
                                                    <option key={team.id} value={team.id}>{team.name}</option>
                                                )}
                                            </select>
                                        </td>
                                        <td className="table__text--align">
                                            {
                                                item.teamMembers.map(member =>
                                                    <div key={member.id} >
                                                        <span className="input marginRight10">{member.name}</span>
                                                        <button type="button" className="close makeitred" onClick={() => this.removeItem(index, member.id)} >&times;</button>
                                                        <br />
                                                    </div>
                                                )
                                            }
                                        </td>
                                        <td className="table__text--align">
                                            <a className={item.notes === "" ? "" : "makeitgreen"} href="#" onClick={(e) => this.editNotes(e, index)}>
                                                <span className="fas fa-pencil-alt edit--icon alignIconCenter"></span>
                                            </a>
                                        </td>
                                        <td>
                                            <div className="delete--tablecell">
                                                <a className={this.state.systemCompanyId === 3 ? "hidden" : "makeitred"} href="#" onClick={(e) => this.deleteItem(e, index)}>
                                                    <span className="fas fa-times-circle edit--icon alignIconCenter"></span>
                                                </a>
                                            </div>
                                        </td>

                                    </tr>

                                )}
                            </tbody>
                        </table>
                        {this.state.errors["truckAllocation"] ?
                            (this.state.errors["truckAllocation"]).split("\n").map((item: any, key: any) => {
                                return <span className="label errors" key={key}>{item}<br /></span>
                            })
                            : ""}
                    </div>

                </div>
                <div className="defaultbutton__container--right">
                    <button className="defaultbutton" type="button" onClick={this.save} disabled={this.state.saveDisabled}>Save</button>
                </div>
            </div>
        );

    }

};



